/*
Written by Kevin Kasey and Austin Thibodeaux in June 2021
Documented by Austin Thibodeaux in April 2023

Component name:     QuartermasterMainPage
Purpose:            Displays offshore locations, in-transit flights, and the
                    onboard/expected/departing passengers for the current selection.
*/

import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faBed,
	faHelicopter,
	faLevelDownAlt,
	faLevelUpAlt,
	faMapMarkerAlt,
	faShip,
	faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, Col, Drawer, Icon, Layout, Row, Spin } from 'antd';
import NonIdealState from 'components/NonIdealState';
import { DragDropProvider } from 'context/drag-drop-provider';
import React from 'react';
import { AddPob } from '../form/AddPob';
import { EditPob } from '../form/EditPob';
import InTransitList from '../inTransitList';
import QuartermasterPageHeader from '../header/quartermaster-page-header';
import DepartingPobList from '../lists/departing/departing-list';
import ExpectedPobList from '../lists/expected/expected-list';
import OnboardPobList from '../lists/onboard/onboard-list';
import PobTransitionControls from '../lists/pob-transition-controls';
import OffshoreLocList from '../offshoreLocList';
import { PobSection, QMCurrentStateCtx } from '../state';
import { useQuartermaster } from '../hooks/use-quartermaster';
import PobDepartureGuidance from './pob-departure-guidance';
import './quartermaster.less';

library.add(faMapMarkerAlt, faUsers, faBed, faLevelUpAlt, faLevelDownAlt, faHelicopter, faShip);

const { Sider } = Layout;

export interface QuartermasterMainPageProps {}

const QuartermasterMainPage: React.FC<QuartermasterMainPageProps> = () => {
	const quartermaster = useQuartermaster();
	const {
		state,
		dispatch,
		contextValue,
		rigData,
		passengerLists,
		inTransit,
		expectedSelectionEnabled,
		departingSelectionEnabled,
		selectedPobIds,
		loadingExpectedPassengers,
		loadingDepartingPassengers,
		submittingNewPobNode,
		sidebarLoading,
		pageLoading,
		setAddPobDrawerOpen,
		setEditPobDrawerOpen,
		setPobSearchInput,
		togglePobSelection,
		setAllPobSelected,
		confirmLoadPassengers,
		confirmDepartPassengers,
		handleOnDragEnd,
		handleAddPobSubmit,
	} = quartermaster;

	function selectionProps(section: PobSection) {
		const selectionEnabled =
			section === 'expected'
				? expectedSelectionEnabled
				: section === 'departing' && departingSelectionEnabled;

		return {
			section,
			selectionEnabled,
			selectedPobIds: selectedPobIds[section],
			onPobSelectionChange: togglePobSelection,
		};
	}

	function transitionControls(section: 'expected' | 'departing') {
		const isExpected = section === 'expected';

		return (
			<PobTransitionControls
				enabled={
					isExpected
						? expectedSelectionEnabled
						: departingSelectionEnabled
				}
				passengerCount={passengerLists[section].length}
				selectedCount={selectedPobIds[section].length}
				actionLabel={isExpected ? 'Load' : 'Depart'}
				tooltip={
					isExpected
						? 'Loads passengers onboard the location and pulls them off their flight'
						: 'Departs passengers from the location and marks them as departed'
				}
				loading={
					isExpected
						? loadingExpectedPassengers
						: loadingDepartingPassengers
				}
				onSelectAll={(selected) => setAllPobSelected(section, selected)}
				onAction={() =>
					isExpected
						? confirmLoadPassengers(section)
						: confirmDepartPassengers(section)
				}
			/>
		);
	}

	let rightPanel = state.sidebarState.rigId ? (
		<>
			<QuartermasterPageHeader
				title={state.sidebarState.locationName}
				value={state.pobSearchInput}
				onChange={setPobSearchInput}
			/>

			<Layout>
				<Row type='flex' style={{ width: '100%', height: '100%' }}>
					<Col
						style={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Row
							type='flex'
							style={{
								flexDirection: 'column',
								flex: 1,
								flexBasis: 0,
								minHeight: 0,
							}}
						>
							<Col
								style={{
									flex: 1,
									padding: '0.5rem',
									paddingRight: 0,
								}}
							>
								<OnboardPobList
									data={passengerLists.onboard}
									{...selectionProps('onboard')}
								/>
							</Col>
						</Row>
					</Col>

					<Col
						style={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Row
							type='flex'
							style={{
								flexDirection: 'column',
								flex: 1,
								flexBasis: 0,
								minHeight: 0,
							}}
						>
							<Col
								style={{
									flex: 1,
									padding: '0.5rem 0.5rem 0 0.5rem',
								}}
							>
								<Card
									style={{ height: '100%' }}
									className='mc-qm-card mc-qm-card-expected'
									bodyStyle={{ height: '100%', padding: 0 }}
									title={
										<>
											<FontAwesomeIcon icon={['fas', 'level-down-alt']} />
											Expected Passengers ({passengerLists.expected.length})
										</>
									}
									extra={transitionControls('expected')}
									size='small'
								>
									<ExpectedPobList
										data={passengerLists.expected}
										{...selectionProps('expected')}
									/>
								</Card>
							</Col>

							<Col style={{ flex: 1, padding: '0.5rem' }}>
								<Card
									style={{ height: '100%' }}
									className='mc-qm-card mc-qm-card-departing'
									bodyStyle={{ height: '100%', padding: 0 }}
									title={
										<>
											<FontAwesomeIcon icon={['fas', 'level-up-alt']} />
											Departing Passengers ({passengerLists.departing.length})
										</>
									}
									extra={transitionControls('departing')}
									size='small'
								>
									<DepartingPobList
										data={passengerLists.departing}
										{...selectionProps('departing')}
									/>
								</Card>
							</Col>
						</Row>
					</Col>
				</Row>
			</Layout>
		</>
	) : (
		<NonIdealState title='Choose an offshore location' icon='exclamation-circle' />
	);

	return (
		<DragDropProvider onDragEnd={handleOnDragEnd}>
			<QMCurrentStateCtx.Provider value={contextValue}>
				<Drawer
					visible={state.drawerState.addPobOpen}
					title='Add Passenger'
					onClose={() => setAddPobDrawerOpen(false)}
					width='calc(100% - 200px)'
					bodyStyle={{
						padding: 0,
						height: 'calc(100% - 55px)',
					}}
					destroyOnClose
				>
					<AddPob onSubmit={handleAddPobSubmit} submitting={submittingNewPobNode} />
				</Drawer>

				<Drawer
					visible={state.drawerState.editPobOpen}
					onClose={() => setEditPobDrawerOpen(false)}
					width={window.innerWidth > 900 ? '1200px' : '100%'}
					bodyStyle={{
						padding: 0,
						height: 'calc(100% - 55px)',
					}}
					destroyOnClose
					closable={false}
				>
					<EditPob
						pobNodeID={state.drawerState.editPobID}
						headerExtra={[
							<Col key='close'>
								<Button
									icon='close'
									type='danger'
									style={{ position: 'relative', top: '2px' }}
									onClick={() => setEditPobDrawerOpen(false)}
								/>
							</Col>,
						]}
					/>
				</Drawer>

				<Layout style={{ height: '100%' }} className='mc-quartermaster'>
					<Sider className='mc-qm-sider' width='25rem'>
						<div className='header transparent'>
							<input
								className='search-input'
								placeholder='Search'
								value={state.searchValue}
								onChange={(event) =>
									dispatch({
										type: 'set-search',
										value: event.target.value,
									})
								}
							/>
						</div>
						<Spin
							spinning={sidebarLoading}
							indicator={<Icon type='loading' />}
							wrapperClassName='mc-qm-sidebar-data-spinner'
						>
							<div className='mc-qm-sidebar-data'>
								<OffshoreLocList
									rigData={rigData}
									style={{ flex: 1 }}
									onOffshoreLocClick={(id, name) =>
										dispatch({ type: 'select-location', id, name })
									}
									selectedID={state.sidebarState.rigId}
								/>
								<InTransitList
									data={inTransit}
									style={{ flex: 1 }}
									onInTransitLocClick={(id, name) =>
										dispatch({ type: 'select-transit', id, name })
									}
									selectedID={state.sidebarState.carrierId}
									offshoreLocationSelected={Boolean(
										state.sidebarState.rigId
									)}
								/>
							</div>
						</Spin>
					</Sider>

					<Spin
						spinning={pageLoading}
						indicator={<Icon type='loading' />}
						wrapperClassName='mc-qm-page-spinner'
					>
						<Layout style={{ height: '100%', overflow: 'hidden' }}>
							<PobDepartureGuidance />
							{rightPanel}
						</Layout>
					</Spin>
				</Layout>
			</QMCurrentStateCtx.Provider>
		</DragDropProvider>
	);
};

export { QMCurrentStateCtx };
export default QuartermasterMainPage;
